import type { ValidationError } from "@tanstack/react-form";
import { useForm } from "@tanstack/react-form";
import { isAxiosError } from "axios";
import { useNavigate, useParams } from "react-router-dom";

import { useResetPassword } from "@/api/auth";
import { InsideLabelFieldPassword } from "@/components/form";
import AuthPageTitle from "@/components/layout/auth-page-title";
import LoginReturnFooter from "@/components/layout/login-return-footer";
import {
  resetPasswordDefaultValues,
  resetPasswordSchema,
} from "@/schemas/auth";
import { stopPropagation } from "@/utils/form";

const TIMEOUT_MESSAGE_ERROR =
  "Temps dépassé, veuillez demander un nouveau lien";

const ResetPasswordPage = () => {
  const { mutateAsync } = useResetPassword();
  const navigate = useNavigate();
  const params = useParams();
  const { email, code } = params;

  const { Field, Subscribe, handleSubmit } = useForm({
    defaultValues: {
      ...resetPasswordDefaultValues,
      code,
      email,
    },
    validators: {
      onSubmit: ({ value }) => {
        const returnValue: { fields: { [k: string]: ValidationError } } = {
          fields: {},
        };
        const { password, passwordConfirmation } = value;
        if (
          !password.match(
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>-_])/
          )
        ) {
          const passwordRegexError: ValidationError =
            'Votre mot de passe doit contenir au moins une majuscule, une minuscule, un chiffre et un caractère spécial (!@#$%^&*(),.?"{}:|<>)';
          returnValue.fields["password"] = passwordRegexError;
        }
        if (password.length < 8) {
          const passwordLengthError: ValidationError =
            "Votre mot de passe doit contenir au moins 8 caractères";
          returnValue.fields["password"] = passwordLengthError;
        }
        if (password !== passwordConfirmation) {
          const passwordMatchError: ValidationError =
            "Les mots de passe ne correspondent pas";
          returnValue.fields["passwordConfirmation"] = passwordMatchError;
        }
        return returnValue;
      },
    },
    onSubmit: async ({ value, formApi }) => {
      const values = resetPasswordSchema.parse(value);
      mutateAsync(values, {
        onSuccess: () => {
          navigate("/reset-mot-de-passe/success");
        },
        onError: (error) => {
          if (isAxiosError(error)) {
            if (error.response?.data.code === "ERROR_TIMEOUT") {
              formApi.setFieldMeta("passwordConfirmation", (prev) => ({
                ...prev,
                errors: [TIMEOUT_MESSAGE_ERROR],
                errorMap: {
                  onChange: TIMEOUT_MESSAGE_ERROR,
                },
              }));
            }
          }
        },
      });
    },
  });

  return (
    <form
      onSubmit={(e) => stopPropagation(e, handleSubmit)}
      className="grid grid-flow-row grid-cols-1 gap-12 pr-[112px] pl-[112px] w-full"
    >
      <AuthPageTitle
        title="Nouveau mot de passe"
        subTitle="Définissez votre nouveau mot de passe"
      />

      <InsideLabelFieldPassword
        Field={Field}
        name="password"
        label="Nouveau mot de passe"
      />
      <InsideLabelFieldPassword
        Field={Field}
        name="passwordConfirmation"
        label="Confirmation du mot de passe"
      />

      <LoginReturnFooter Subscribe={Subscribe} />
    </form>
  );
};

export default ResetPasswordPage;
